/** Formatting and IST range helpers for the staff attendance screens. */

const IST = 'Asia/Kolkata';
const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: IST });

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Today's IST date as YYYY-MM-DD. */
export const istToday = () => dayFmt.format(new Date());

const parse = (key: string) => new Date(`${key}T00:00:00Z`);
const key = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (k: string, n: number) => key(new Date(parse(k).getTime() + n * 864e5));

export type RangeMode = 'day' | 'week' | 'month' | 'custom';

/** The range containing `anchor` for a mode. Weeks run Monday–Sunday. */
export function rangeFor(mode: RangeMode, anchor: string): { from: string; to: string } {
  if (mode === 'day') return { from: anchor, to: anchor };
  if (mode === 'week') {
    const wd = parse(anchor).getUTCDay(); // 0 = Sun
    const from = addDays(anchor, -((wd + 6) % 7));
    return { from, to: addDays(from, 6) };
  }
  const d = parse(anchor);
  const from = key(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
  const to = key(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
  return { from, to };
}

/** Moves the anchor one step back (-1) or forward (+1) for a mode. */
export function shiftAnchor(mode: RangeMode, anchor: string, dir: -1 | 1): string {
  if (mode === 'day') return addDays(anchor, dir);
  if (mode === 'week') return addDays(anchor, dir * 7);
  const d = parse(anchor);
  return key(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + dir, 1)));
}

export function rangeLabel(mode: RangeMode, from: string, to: string): string {
  const fmt = (k: string, opts: Intl.DateTimeFormatOptions) =>
    parse(k).toLocaleDateString('en-IN', { timeZone: 'UTC', ...opts });
  if (mode === 'day') return fmt(from, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  if (mode === 'month') return fmt(from, { month: 'long', year: 'numeric' });
  return `${fmt(from, { day: 'numeric', month: 'short' })} – ${fmt(to, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export const dateLabel = (k: string) =>
  parse(k).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

/** "9:43 am" in IST. */
export const istTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit' })
    : '—';

/** "7h 43m", "45m", "—". */
export const hm = (minutes?: number | null) => {
  if (!minutes) return minutes === 0 ? '0m' : '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
};

/** "09:00" → "9:00 am". */
export const hhmmLabel = (hhmm?: string | null) => {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
};

/** IST "YYYY-MM-DDTHH:mm" for a datetime-local input, from an ISO instant. */
export const toIstLocalInput = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(new Date(iso).getTime() + 5.5 * 3600e3);
  return d.toISOString().slice(0, 16);
};

/** ISO instant from an IST "YYYY-MM-DDTHH:mm" datetime-local value. */
export const fromIstLocalInput = (v: string) => (v ? new Date(`${v}:00+05:30`).toISOString() : '');

/** Owner or super admin may edit; managers view only. Accepts brand-branches owner roles too. */
export const canEditAttendance = (role?: string) => !!role && !['manager', 'instructor'].includes(role);
